import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { User } from "@supabase/supabase-js";
import { Settings } from "lucide-react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useProfile } from "@/hooks/useProfile";
import { useEffect } from "react";
import { PersonalInfoSection } from "@/components/profile/tabs/form/sections/PersonalInfoSection";
import { ProfessionalInfoSection } from "@/components/profile/tabs/form/sections/ProfessionalInfoSection";
import { AddressSection } from "@/components/profile/tabs/form/sections/AddressSection";
import { mapProfileToFormDefaults, buildProfileUpdate } from "@/services/profile/profileMappers";

const profileFormSchema = z.object({
  title: z.string().optional(),
  firstName: z.string().min(1, "First name is required"),
  middleName: z.string().optional(),
  lastName: z.string().min(1, "Last name is required"),
  dateOfBirthMonth: z.string().optional(),
  dateOfBirthDay: z.string().optional(),
  dateOfBirthYear: z.string().optional(),
  streetNumber: z.string().optional(),
  streetName: z.string().optional(),
  suiteAptUnit: z.string().optional(),
  city: z.string().optional(),
  provinceState: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
  phoneNumber: z.string().optional(),
  citizenship: z.string().optional(),
  isAccredited: z.boolean().default(false),
  defaultCurrency: z.string().optional(),
  professionalDesignations: z.array(z.string()).default([]),
  // Accept empty string or a valid URL
  linkedinProfile: z.union([z.string().url(), z.literal("")]).optional(),
});

export type ProfileFormData = z.infer<typeof profileFormSchema>;

interface ProfileFormProps {
  user: User;
}

export function ProfileForm({ user }: ProfileFormProps) {
  const { profile, isLoading, updateProfile } = useProfile(user);
  
  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      title: "",
      firstName: "",
      middleName: "",
      lastName: "",
      dateOfBirthMonth: "",
      dateOfBirthDay: "",
      dateOfBirthYear: "",
      streetNumber: "",
      streetName: "",
      suiteAptUnit: "",
      city: "",
      provinceState: "",
      postalCode: "",
      country: "Canada",
      phoneNumber: "",
      citizenship: "Canada",
      isAccredited: false,
      defaultCurrency: "CAD",
      professionalDesignations: [],
      linkedinProfile: "",
    },
  });

  useEffect(() => {
    if (profile) {
      const defaults = mapProfileToFormDefaults(profile as any);
      form.reset(defaults);
    }
  }, [profile, form]);

  const onSubmit = async (values: ProfileFormData) => {
    const updateData = buildProfileUpdate(values);
    await updateProfile(updateData as any);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="w-full max-w-4xl mx-auto px-2 sm:px-0">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 md:space-y-8">
          {/* Personal Information Section */}
          <PersonalInfoSection form={form} user={user} />

          {/* Professional Information Section */}
          {(profile?.persona === 'advisor' || profile?.persona === 'asset_manager') && (
            <ProfessionalInfoSection form={form} user={user} />
          )}

          {/* Address Information Section */}
          <AddressSection form={form} />
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 pt-6 border-t">
          <Button type="submit" className="w-full sm:w-auto min-h-[44px]">
            <Settings className="mr-2 h-4 w-4" />
            Update Profile
          </Button>
        </div>
      </form>
    </Form>
    </div>
  );
}
